/* Product pagination is progressive enhancement. Ordinary page links remain crawlable. */
(() => {
  if (customElements.get('park-product-pagination')) return;
  const snapshots = new Map();
  const cacheLimit = 6;
  let restoreRevision = 0;
  let restoreController;
  const key = id => `park-products:${id}`;
  const section = () => document.querySelector('[data-park-product-listing]');
  const capture = root => Array.from(root.querySelectorAll('[data-ajax-container]'), el => ({name: el.dataset.ajaxContainer, html: el.innerHTML}));
  function remember(state, content) {
    snapshots.delete(state.id);
    snapshots.set(state.id, content);
    while (snapshots.size > cacheLimit) snapshots.delete(snapshots.keys().next().value);
    try {
      sessionStorage.setItem(key(state.id), JSON.stringify(content));
      const keys = Object.keys(sessionStorage).filter(name => name.startsWith('park-products:')).sort();
      keys.slice(0, Math.max(0, keys.length - cacheLimit)).forEach(name => sessionStorage.removeItem(name));
    } catch (_) { /* Memory still supports in-page history. */ }
  }
  function stored(state) {
    if (snapshots.has(state.id)) return snapshots.get(state.id);
    try { return JSON.parse(sessionStorage.getItem(key(state.id))); } catch (_) { return null; }
  }
  const makeState = () => ({id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, url: location.href, y: scrollY});
  function initialize() {
    if (window.theme?.initAnimateOnScroll) window.theme.initAnimateOnScroll();
    const root = section();
    if (window.jQuery && root?.dataset.components) root.dataset.components.split(',').forEach(component => window.jQuery(document).trigger('cc:component:load', [component, root]));
  }
  async function page(url, sectionId, signal) {
    const target = new URL(url, location.href);
    if (target.origin !== location.origin) throw new Error('Invalid pagination origin');
    target.searchParams.set('section_id', sectionId);
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) controller.abort();
    signal?.addEventListener('abort', abort, {once:true});
    const timeout = setTimeout(abort, 8000);
    let response, html;
    try {
      response = await fetch(target, {credentials: 'same-origin', signal: controller.signal});
      html = await response.text();
    } finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
    if (!response.ok) throw new Error('Product request failed');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.querySelector('park-product-pagination') || !doc.querySelector('[data-ajax-container="products"] .product-list')) throw new Error('Invalid product response');
    return doc;
  }
  async function remaining(root, firstURL, signal) {
    let next = firstURL;
    const fragment = document.createDocumentFragment();
    const visited = new Set();
    while (next) {
      const absolute = new URL(next, location.href).href;
      if (visited.has(absolute)) throw new Error('Repeated page');
      visited.add(absolute);
      const doc = await page(absolute, root.dataset.filterSectionId, signal);
      // Promo cards belong to the initial grid, never repeat them on appended pages.
      doc.querySelectorAll('[data-ajax-container="products"] .product-list > [data-product-id]').forEach(el => fragment.append(el));
      next = doc.querySelector('[data-load-products]')?.getAttribute('href');
    }
    return fragment;
  }
  async function restore(state) {
    const revision = ++restoreRevision;
    restoreController?.abort();
    const controller = restoreController = new AbortController();
    try {
    const root = section();
    if (!root) return;
    let content = stored(state);
    if (!content) {
      const doc = await page(state.base || state.url, root.dataset.filterSectionId, controller.signal);
      if (state.expanded) {
        const firstURL = doc.querySelector('[data-load-products]')?.getAttribute('href');
        if (firstURL) doc.querySelector('.product-list').append(await remaining(root, firstURL, controller.signal));
        doc.querySelector('[data-ajax-container="pagination"]').innerHTML = '';
      }
      content = capture(doc);
    }
    if (revision !== restoreRevision || !root.isConnected) return;
    content.forEach(item => {
      const el = Array.from(root.querySelectorAll('[data-ajax-container]')).find(node => node.dataset.ajaxContainer === item.name);
      if (el) el.innerHTML = item.html;
    });
    initialize();
    requestAnimationFrame(() => { if (revision === restoreRevision) scrollTo(0, state.y); });
    } catch (error) { if (revision === restoreRevision) throw error; }
  }
  window.addEventListener('popstate', event => {
    const state = event.state?.parkProducts;
    if (!state) { ++restoreRevision; restoreController?.abort(); return; }
    event.stopImmediatePropagation();
    restore(state).catch(() => location.reload());
  }, true);
  function savePosition() {
    const state = history.state?.parkProducts;
    if (state && state.url === location.href) history.replaceState({...history.state, parkProducts: {...state, y: scrollY}}, '', location.href);
  }
  document.addEventListener('pointerdown', savePosition, true);
  document.addEventListener('change', savePosition, true);
  window.addEventListener('pagehide', () => {
    const state = history.state?.parkProducts;
    const root = section();
    if (!state || !root || state.url !== location.href) return;
    state.y = scrollY;
    remember(state, capture(root));
    history.replaceState({...history.state, parkProducts: state}, '', location.href);
  });
  class ProductPagination extends HTMLElement {
    disconnectedCallback() { this.request?.abort(); }
    connectedCallback() {
      this.setAttribute('data-ready', '');
      const link = this.querySelector('[data-load-products]');
      if (!link || link.parkBound) return;
      link.parkBound = true;
      link.addEventListener('click', async event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        if (this.getAttribute('aria-busy') === 'true') return;
        const root = this.closest('[data-filter-section-id]');
        const base = location.href;
        const controller = this.request = new AbortController();
        ++restoreRevision; restoreController?.abort();
        const status = this.querySelector('[role="status"]');
        this.setAttribute('aria-busy', 'true');
        status.textContent = this.dataset.loading;
        try {
          const fragment = await remaining(root, link.href, controller.signal);
          if (controller.signal.aborted || !this.isConnected || location.href !== base) return;
          this.removeAttribute('aria-busy');
          status.textContent = '';
          const before = makeState();
          remember(before, capture(root));
          history.replaceState({...history.state, parkProducts: before}, '', base);
          const firstNew = fragment.querySelector('a');
          root.querySelector('[data-ajax-container="products"] .product-list').append(fragment);
          const nextURL = new URL(link.href);
          const after = {...makeState(), url: nextURL.href, base, expanded: true};
          this.parentElement.innerHTML = '';
          remember(after, capture(root));
          history.pushState({parkProducts: after}, '', nextURL);
          initialize();
          firstNew?.focus({preventScroll: true});
        } catch (_) {
          status.textContent = this.dataset.error;
          this.removeAttribute('aria-busy');
        }
      });
    }
  }
  // Saved markup must reconnect its event handler after a history restoration.
  customElements.define('park-product-pagination', ProductPagination);
  if (history.state?.parkProducts) restore(history.state.parkProducts).catch(() => {});
})();
